import { useCallback, useState } from "react";
import {DataGrid, useSnackbar} from "@dain/mui-components";
import LinearProgress from "@mui/material/LinearProgress";
import { GridColDef } from "@mui/x-data-grid-pro";
import {CommonTranslKey} from "../CommonTranslKey";
import {useTranslation} from "react-i18next";

export interface PageableGridProps<T> {
    rowsData: T[];
    totalRowsCount: number;
    columns: GridColDef[];
    onFetchMore: () => void;

    catalogLoading?: boolean;

    onRowSelect?: (row: T) => void;
    selectedRowId?: number;
    onRowDoubleClick?: (rowId: number) => void;
}

export enum PageLoadSize {
    INITIAL_PAGE_SIZE = 40,
    NEXT_PAGE_SIZE = 20
}

export default function PageableGrid<T extends { id: number }>(props: Readonly<PageableGridProps<T>>) {
    const {rowsData, totalRowsCount, columns, selectedRowId, onFetchMore, catalogLoading, onRowSelect, onRowDoubleClick} = props;

    const {t} = useTranslation();
    const snackbar = useSnackbar();

    const [isFetching, setIsFetching] = useState(false);

    const handleScrollToEnd = useCallback(async () => {
        if (isFetching) {
            return;
        }
        setIsFetching(true);
        try {
            onFetchMore();
        } catch (error) {
            snackbar.error(t(CommonTranslKey.UNEXPECTED_ERROR), error);
        } finally {
            setIsFetching(false);
        }
    }, [isFetching, onFetchMore, snackbar, t]);

    const handleRowSelectionChange = (rowSelectionModel: any) => {
        const selectedRow = rowsData.find((row) => row.id === rowSelectionModel[0]);
        if (selectedRow && selectedRow.id !== selectedRowId) {
            onRowSelect?.(selectedRow);
        }
    };

    return (
        <DataGrid
            rows={rowsData}
            columns={columns}
            rowCount={totalRowsCount}
            pagination={false}
            rowSelectionModel={selectedRowId ? [selectedRowId] : []}
            onRowSelectionModelChange={handleRowSelectionChange}
            onRowDoubleClick={(params) => onRowDoubleClick?.(params.row.id)}
            loading={isFetching || catalogLoading}
            hideFooterPagination
            sx={{ minHeight: 150, maxHeight: 500 }}
            onRowsScrollEnd={handleScrollToEnd}
            slots={{
                loadingOverlay: LinearProgress,
            }}
        />
    );
}
